/**
 * Type-safe CSS variable token references for sdweb.
 *
 * Usage:
 *   import { tokens } from 'styles/tokens';
 *   const Div = styled.div`color: ${tokens.text.primary};`;
 *
 * Sources:
 *   - @kcftech/design-system/tokens - semantic tokens (text, surface, border)
 *   - src/styles/tokens/semantics.css - sdweb-specific tokens (charts, lozenges, nav)
 *
 * To regenerate sdweb tokens: pnpm generate:tokens
 */
import { tokens as dsTokens } from '@kcftech/design-system/tokens';

import { sdwebTokens } from './sdweb-tokens.generated';

/**
 * Deep-merges two token maps at the group level.
 * Groups present in both are merged entry-by-entry (sd wins on key conflict).
 * Groups present in only one are passed through as-is.
 */
function mergeTokenGroups<
    DS extends Record<string, Record<string, string>>,
    SD extends Record<string, Record<string, string>>,
>(ds: DS, sd: SD): DS & SD {
    const result: Record<string, Record<string, string>> = { ...ds };
    for (const [group, entries] of Object.entries(sd)) {
        result[group] = { ...(result[group] ?? {}), ...entries };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return result as any;
}

export const tokens = mergeTokenGroups(dsTokens, sdwebTokens);

export type Tokens = typeof tokens;
export type TokenGroup = keyof Tokens;
/** All token values within a specific group (e.g. TokenValue<"text"> = "var(--text-primary)" | ...) */
export type TokenValue<G extends TokenGroup> = Tokens[G][keyof Tokens[G]];
